import { devError } from '../utils/devLogger';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import { Modal, ListGroup } from 'react-bootstrap';

const LearnerMobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      devError('Failed to log out', error);
    }
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navItems = [
    {
      path: '/dashboard',
      label: 'Home',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      )
    },
    {
      path: '/resources',
      label: 'Resources',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      )
    },
    {
      path: '/learner/grades',
      label: 'Grades',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      )
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      )
    },
    {
      label: 'More',
      isMore: true,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="1"></circle>
          <circle cx="19" cy="12" r="1"></circle>
          <circle cx="5" cy="12" r="1"></circle>
        </svg>
      )
    }
  ];

  const moreMenuItems = [
    { path: '/messages', label: 'Messages', shortLabel: 'Msg' },
    { path: '/attendance', label: 'Attendance', shortLabel: 'Att' },
    { path: '/assignments', label: 'Assignments', shortLabel: 'Asn' },
    { path: '/support', label: 'Support', shortLabel: 'Sup' },
  ];

  const handleNavClick = (item) => {
    if (item.isMore) {
      setShowMoreMenu(true);
    } else {
      navigate(item.path);
    }
  };

  const styles = {
    container: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'var(--white)',
      borderTop: '1px solid var(--gray-border)',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '8px 0 calc(8px + env(safe-area-inset-bottom))',
      zIndex: 1050,
      boxShadow: '0 -8px 28px rgba(23, 36, 49, 0.1)',
    },
    navItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '8px 12px',
      cursor: 'pointer',
      color: 'var(--brand-text-muted)',
      transition: 'var(--transition-fast)',
      textDecoration: 'none',
      border: 'none',
      background: 'none',
      minWidth: '60px',
    },
    navItemActive: {
      color: 'var(--brand-primary)',
    },
    navLabel: {
      fontSize: '11px',
      marginTop: '4px',
      fontWeight: '500',
    },
    // Spacer for content above the nav
    spacer: {
      height: 'calc(70px + env(safe-area-inset-bottom))',
    }
  };

  return (
    <>
      {/* Spacer to prevent content from being hidden behind nav */}
      <div style={styles.spacer} className="d-lg-none"></div>

      {/* Mobile Bottom Navigation - only visible on mobile */}
      <nav style={styles.container} className="d-lg-none">
        {navItems.map((item, index) => (
          <button
            key={index}
            onClick={() => handleNavClick(item)}
            style={{
              ...styles.navItem,
              ...(item.path && isActive(item.path) ? styles.navItemActive : {})
            }}
          >
            <span style={{ opacity: item.path && isActive(item.path) ? 1 : 0.7 }}>
              {item.icon}
            </span>
            <span style={{
              ...styles.navLabel,
              fontWeight: item.path && isActive(item.path) ? '600' : '500'
            }}>
              {item.label}
            </span>
          </button>
        ))}
      </nav>

      {/* More Menu Modal */}
      <Modal
        show={showMoreMenu}
        onHide={() => setShowMoreMenu(false)}
        centered
        className="mobile-more-menu"
      >
        <Modal.Header closeButton>
          <Modal.Title>More</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ListGroup variant="flush">
            {moreMenuItems.map((item, index) => (
              <ListGroup.Item
                key={index}
                action
                onClick={() => {
                  setShowMoreMenu(false);
                  navigate(item.path);
                }}
                className="d-flex align-items-center py-3"
              >
                <span className="badge bg-light text-dark me-3">{item.shortLabel}</span>
                <span style={{ fontSize: '16px' }}>{item.label}</span>
              </ListGroup.Item>
            ))}
            <ListGroup.Item
              action
              onClick={() => {
                setShowMoreMenu(false);
                handleLogout();
              }}
              className="d-flex align-items-center py-3 text-danger"
            >
              <span className="badge bg-light text-dark me-3">Out</span>
              <span style={{ fontSize: '16px' }}>Logout</span>
            </ListGroup.Item>
          </ListGroup>
        </Modal.Body>
      </Modal>

      {/* CSS for hiding desktop navbar on mobile and vice versa */}
      <style>{`
        @media (max-width: 991.98px) {
          .desktop-navbar {
            display: none !important;
          }
          .mobile-nav-spacer {
            height: calc(70px + env(safe-area-inset-bottom));
          }
        }

        @media (min-width: 992px) {
          .mobile-bottom-nav {
            display: none !important;
          }
        }

        .mobile-more-menu .modal-content {
          border-radius: 16px 16px 0 0;
          margin-bottom: 0;
        }

        .mobile-more-menu .modal-dialog {
          margin: 0;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          max-width: 100%;
        }

        .mobile-more-menu .list-group-item:active {
          background-color: var(--gray-light);
        }
      `}</style>
    </>
  );
};

export default LearnerMobileNav;
